import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save } from "lucide-react";
import {
  adminGetTerms,
  adminUpdateTermsSettings,
  adminUpsertTermsSection,
  adminDeleteTermsSection,
  type TermsSectionRow,
} from "@/lib/quote-terms.functions";

export const Route = createFileRoute(
  "/_authenticated/admin/algemene-voorwaarden",
)({
  head: () => ({
    meta: [
      { title: "Admin — Algemene voorwaarden beheren" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminTermsPage,
});

type SectionDraft = {
  id?: string;
  title: string;
  paragraphsText: string; // één alinea per lege regel-groep, gescheiden door blanko regel
  sort_order: number;
  is_published: boolean;
};

const toDraft = (r: TermsSectionRow): SectionDraft => ({
  id: r.id,
  title: r.title,
  paragraphsText: r.paragraphs.join("\n\n"),
  sort_order: r.sort_order,
  is_published: r.is_published,
});

const paragraphsFromText = (t: string): string[] =>
  t
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

function AdminTermsPage() {
  const { isAdmin, loading } = useAuth();
  const get = useServerFn(adminGetTerms);
  const saveSettings = useServerFn(adminUpdateTermsSettings);
  const upsert = useServerFn(adminUpsertTermsSection);
  const del = useServerFn(adminDeleteTermsSection);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-quote-terms"],
    queryFn: () => get({}),
    enabled: isAdmin,
  });

  const [title, setTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [drafts, setDrafts] = useState<SectionDraft[]>([]);

  useEffect(() => {
    if (data) {
      setTitle(data.settings.title);
      setIntro(data.settings.intro);
      setDrafts(data.sections.map(toDraft));
    }
  }, [data]);

  const settingsMut = useMutation({
    mutationFn: () => saveSettings({ data: { title, intro } }),
    onSuccess: () => {
      toast.success("Titel & intro opgeslagen");
      qc.invalidateQueries({ queryKey: ["admin-quote-terms"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const upsertMut = useMutation({
    mutationFn: (d: SectionDraft) =>
      upsert({
        data: {
          id: d.id,
          title: d.title,
          paragraphs: paragraphsFromText(d.paragraphsText),
          sort_order: d.sort_order,
          is_published: d.is_published,
        },
      }),
    onSuccess: () => {
      toast.success("Sectie opgeslagen");
      qc.invalidateQueries({ queryKey: ["admin-quote-terms"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Sectie verwijderd");
      qc.invalidateQueries({ queryKey: ["admin-quote-terms"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateDraft = (idx: number, patch: Partial<SectionDraft>) => {
    setDrafts((cur) => cur.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };

  const addNew = () => {
    const nextOrder = drafts.length
      ? Math.max(...drafts.map((d) => d.sort_order)) + 10
      : 10;
    setDrafts((cur) => [
      ...cur,
      {
        title: "Nieuwe sectie",
        paragraphsText: "",
        sort_order: nextOrder,
        is_published: true,
      },
    ]);
  };

  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin)
    return <div className="p-8">Alleen beheerders hebben toegang.</div>;

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-bold">Algemene voorwaarden beheren</h1>
        <p className="text-sm text-muted-foreground">
          Deze tekst wordt automatisch meegestuurd in elke offerte-bundel (PDF).
          Wijzigingen zijn direct actief bij het volgende gegenereerde document.
        </p>
      </header>

      {isLoading ? (
        <div>Voorwaarden laden…</div>
      ) : (
        <>
          {/* Titel & intro */}
          <section className="space-y-3 rounded-lg border bg-card p-4">
            <h2 className="font-semibold">Titel &amp; inleiding</h2>
            <div className="space-y-2">
              <Label htmlFor="terms-title">Titel</Label>
              <Input
                id="terms-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="terms-intro">Inleiding</Label>
              <Textarea
                id="terms-intro"
                rows={4}
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
              />
            </div>
            <Button
              onClick={() => settingsMut.mutate()}
              disabled={settingsMut.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {settingsMut.isPending ? "Opslaan…" : "Titel & intro opslaan"}
            </Button>
          </section>

          {/* Secties */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Secties</h2>
              <Button variant="outline" size="sm" onClick={addNew}>
                <Plus className="mr-2 h-4 w-4" />
                Nieuwe sectie
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Scheid meerdere alinea's binnen één sectie met een <strong>lege
              regel</strong>. Volgorde bepaalt de plaats in het document
              (oplopend).
            </p>

            {drafts.map((d, idx) => (
              <div
                key={d.id ?? `new-${idx}`}
                className="space-y-3 rounded-lg border bg-card p-4"
              >
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[220px] space-y-1">
                    <Label>Titel</Label>
                    <Input
                      value={d.title}
                      onChange={(e) => updateDraft(idx, { title: e.target.value })}
                    />
                  </div>
                  <div className="w-28 space-y-1">
                    <Label>Volgorde</Label>
                    <Input
                      type="number"
                      value={d.sort_order}
                      onChange={(e) =>
                        updateDraft(idx, {
                          sort_order: Number(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={d.is_published}
                      onCheckedChange={(v) =>
                        updateDraft(idx, { is_published: v })
                      }
                    />
                    <span className="text-sm">Publiceren</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>Alinea's (splits met een lege regel)</Label>
                  <Textarea
                    rows={6}
                    value={d.paragraphsText}
                    onChange={(e) =>
                      updateDraft(idx, { paragraphsText: e.target.value })
                    }
                  />
                </div>

                <div className="flex justify-between">
                  <Button
                    size="sm"
                    onClick={() => upsertMut.mutate(d)}
                    disabled={upsertMut.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Opslaan
                  </Button>
                  {d.id ? (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        if (confirm(`Sectie "${d.title}" verwijderen?`)) {
                          delMut.mutate(d.id!);
                        }
                      }}
                      disabled={delMut.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Verwijderen
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      Nog niet opgeslagen
                    </span>
                  )}
                </div>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
}
